import React, { useEffect, useId, useRef, useState } from 'react';
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';

const BRAND = '#AE256D';

/** Hero kart / öne çıkan kart — 275×344 */
export const HERO_CARD_WIDTH = 275;
export const HERO_CARD_HEIGHT = 344;
export const HERO_CARD_ASPECT = HERO_CARD_WIDTH / HERO_CARD_HEIGHT;

/**
 * Önce "özel görsel yüklemek ister misiniz?" → Evet ise upload/URL + crop.
 * onSkip: görselsiz devam, onConfirm(imageValue), onCancel: seçimi geri al.
 */
const FeaturedImageAskDialog = ({
  open,
  title,
  onConfirm,
  onSkip,
  onCancel,
  aspect = HERO_CARD_ASPECT,
  width = HERO_CARD_WIDTH,
  height = HERO_CARD_HEIGHT,
}) => {
  const translate = useTranslate();
  const inputId = useId();
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  const [step, setStep] = useState('ask'); // ask | editor
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({});
  const [cropping, setCropping] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [imageValue, setImageValue] = useState(null);
  const [urlInput, setUrlInput] = useState('');

  useEffect(() => {
    if (!open) {
      setStep('ask');
      setSrc(null);
      setCrop({});
      setCropping(false);
      setCropModalOpen(false);
      setPreview(null);
      setImageValue(null);
      setUrlInput('');
    }
  }, [open]);

  const getCroppedImg = (image, percentCrop) => {
    const pixelCrop = convertToPixelCrop(percentCrop, image.width, image.height);
    if (!pixelCrop.width || !pixelCrop.height) {
      return Promise.reject(new Error('Empty crop'));
    }
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = Math.max(1, Math.round(pixelCrop.width * scaleX));
    canvas.height = Math.max(1, Math.round(pixelCrop.height * scaleY));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(blob);
        resolve({ file, previewUrl });
      }, 'image/jpeg');
    });
  };

  const onImageLoaded = (e) => {
    const { width: imgWidth, height: imgHeight } = e.currentTarget;
    setCrop(
      centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, aspect, imgWidth, imgHeight),
        imgWidth,
        imgHeight,
      ),
    );
  };

  const openCropWithDataUrl = (dataUrl) => {
    setSrc(dataUrl);
    setCrop({});
    setCropModalOpen(true);
  };

  const onSelectFile = (e) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.addEventListener('load', () => openCropWithDataUrl(reader.result));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const loadUrlForCrop = async () => {
    const url = urlInput.trim();
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const reader = new FileReader();
      reader.addEventListener('load', () => openCropWithDataUrl(reader.result));
      reader.readAsDataURL(blob);
    } catch {
      // CORS vb. — URL'yi doğrudan kaydet
      setImageValue({ src: url, title: 'url' });
      setPreview(url);
    }
  };

  const applyCrop = async () => {
    if (!imageRef.current || !crop?.width) return;
    setCropping(true);
    try {
      const res = await getCroppedImg(imageRef.current, crop);
      if (res?.file) {
        setPreview(res.previewUrl);
        setImageValue({
          src: res.previewUrl,
          rawFile: res.file,
          title: res.file.name,
        });
      }
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setCropping(false);
      setCropModalOpen(false);
      setSrc(null);
    }
  };

  return (
    <>
      <Dialog open={open && step === 'ask'} onClose={onCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          {title ||
            translate('show.home_placements_ask_image_title', {
              _: 'Özel görsel yüklemek ister misiniz?',
            })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {translate('show.home_placements_ask_image_desc', {
              _: 'Yüklemezseniz etkinlik banner görseli kullanılır. Ölçü hero kart ile aynıdır (275×344).',
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={onCancel}>{translate('ra.action.cancel', { _: 'İptal' })}</Button>
          <Button onClick={onSkip} variant="outlined" sx={{ borderColor: BRAND, color: BRAND }}>
            {translate('show.home_placements_skip_image', { _: 'Hayır, görselsiz ekle' })}
          </Button>
          <Button
            onClick={() => setStep('editor')}
            variant="contained"
            sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#921F5C' } }}
          >
            {translate('show.home_placements_yes_image', { _: 'Evet, görsel yükle' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open && step === 'editor'} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {translate('show.home_placements_image_editor_title', { _: 'Özel görsel' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('heros.featured_crop_hint', {
              _: `Hero kart ölçüsü ${width}×${height}. Kaydetmeden önce kırpın.`,
            })}
          </Typography>

          {preview ? (
            <Box sx={{ mb: 2 }}>
              <img
                src={preview}
                alt="Preview"
                style={{ width, height, objectFit: 'cover', borderRadius: 4, display: 'block' }}
              />
            </Box>
          ) : null}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="contained" onClick={() => fileInputRef.current?.click()}>
              {translate('form.add_image', { _: 'Görsel yükle' })}
            </Button>
            <input
              ref={fileInputRef}
              id={inputId}
              type="file"
              accept="image/*"
              hidden
              onChange={onSelectFile}
            />
          </Box>

          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
            {translate('show.home_placements_or_url', { _: 'veya görsel URL’si' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              fullWidth
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://…"
            />
            <Button variant="outlined" onClick={loadUrlForCrop} disabled={!urlInput.trim()}>
              {translate('show.home_placements_use_url', { _: 'Kullan' })}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCancel}>{translate('ra.action.cancel', { _: 'İptal' })}</Button>
          <Button variant="contained" disabled={!imageValue} onClick={() => onConfirm(imageValue)}>
            {translate('ra.action.confirm', { _: 'Onayla' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cropModalOpen}
        onClose={cropping ? undefined : () => setCropModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {translate('heros.featured_crop_title', { _: `Görseli Kırp (${width}×${height})` })}
        </DialogTitle>
        <DialogContent dividers>
          {src ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                bgcolor: '#111',
                p: 1,
                borderRadius: 1,
              }}
            >
              <ReactCrop crop={crop} aspect={aspect} ruleOfThirds onChange={setCrop}>
                <img
                  ref={imageRef}
                  src={src}
                  alt="Crop"
                  onLoad={onImageLoaded}
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              </ReactCrop>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropModalOpen(false)} disabled={cropping}>
            {translate('ra.action.cancel', { _: 'İptal' })}
          </Button>
          <Button variant="contained" onClick={applyCrop} disabled={cropping || !crop?.width}>
            {cropping
              ? translate('form.cropping', { _: 'Hazırlanıyor…' })
              : translate('form.apply_crop', { _: 'Kırp ve uygula' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeaturedImageAskDialog;
